import { KeyboardEvent, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import CardTooltip from './CardTooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import Avatar from '@/components/Avatar';
import Table from '@/components/Table';
import { TableCell, TableHead, TableHeading, TableRow } from '@/elements/Table';
import { IS_PROD } from '@/constants/env';
import styled from '@/styles';
import type { WorkerListItem } from '@/services/workersV2API';

import SegmentBar from './SegmentBar';
import StatusIcon from './StatusIcon';
import StuckForPill from './StuckForPill';
import { NO_MATCH_TEXT, SortIcon } from './WorkersTable';
import type { SortColumn, SortState } from './WorkersTable';
import {
  bgCheckCleared,
  formatActiveAgo,
  formatPhone,
  formatTaxType,
  i9Cleared,
  i9IconLabel,
  i9Mismatch,
  statusPillLabel,
} from '../mappings';
import {
  bgNeedsInvite,
  bgStatusFor,
  ColumnTab,
  formatLongDate,
  i9NeedsInvite,
  i9StatusFor,
  SegmentTone,
  stepSegmentTone,
  VendorStatus,
} from '../onboardingMappings';

const CHECKR_BASE_URL = IS_PROD
  ? 'https://dashboard.checkr.com'
  : 'https://dashboard.checkrhq-staging.net';

// What these buttons do is an open product question (ticket WL-19, WL-21,
// Q10): no admin endpoint sends a nudge or re-sends an I-9 invitation. A
// Checkr re-send exists (GraphQL workerResendCheckrInvitation) but nothing
// decides when ops may use it here. They show the design but stay disabled
// with the reason (review R5A-05).
export const NOT_AVAILABLE_REASON = 'Not available from this page yet';

// GRAVY-10989: with the side panel open (400px + 16px gap) the list keeps
// about 540px at a 1280px window. Unbroken names, step labels and headers
// made the compact table wider than that, so it ran under the panel and hid
// Stuck for and Nudge. In compact mode they wrap; anything still too wide
// scrolls inside the card (TableScroll), never under the panel.
// BH-60: `break-word`, not `anywhere`. `anywhere` also lowers a cell's
// smallest width to one letter, so below 1280px the table squeezed the name
// column to a letter per line and pushed Nudge past the card. `break-word`
// wraps between words and splits a word only when it cannot fit, so a column
// never gets narrower than its longest word, and the table scrolls instead.
export const COMPACT_WRAP = {
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
};
export const COMPACT_NAME = {
  ...COMPACT_WRAP,
  minWidth: '96px',
  maxWidth: '140px',
};

// `@/components/Table` scrolls sideways only with spacing={false}, a
// different look, so the scroll box is here.
export const TableScroll = styled('div', { width: '100%', overflowX: 'auto' });

const Th = styled(TableHeading, {
  padding: '12px 8px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '#3D3D3D',
  whiteSpace: 'nowrap',
  variants: {
    sortable: { true: { cursor: 'pointer', userSelect: 'none' } },
    compact: { true: { whiteSpace: 'normal' } },
  },
});
const Td = styled(TableCell, {
  padding: '16px 8px',
  verticalAlign: 'top',
  fontSize: '14px',
});
const Row = styled(TableRow, {
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#FAFAFA' },
  variants: {
    selected: {
      // Overrides the shared row's theme tint: the design's light green.
      true: {
        backgroundColor: '#EEF7EC',
        '&:nth-child(even)': { backgroundColor: '#EEF7EC' },
        '&:hover': { backgroundColor: '#EEF7EC' },
      },
    },
  },
});
const Name = styled('button', {
  appearance: 'none',
  display: 'block',
  maxWidth: '180px',
  border: 'none',
  background: 'transparent',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  variants: { compact: { true: COMPACT_NAME } },
});
const Muted = styled('div', { fontSize: '13px', color: '#5A5A5A' });
const Small = styled('div', {
  fontSize: '12px',
  color: '#5A5A5A',
  marginTop: '4px',
});
const ActiveLine = styled('div', {
  fontSize: '12px',
  color: '#8A8A8A',
  variants: { today: { true: { color: '#2E8722' } } },
});
const StepText = styled('div', {
  fontSize: '14px',
  whiteSpace: 'nowrap',
  variants: { compact: { true: COMPACT_WRAP } },
});
const Warning = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '6px',
  fontSize: '13px',
  color: '#A11A1A',
});
const LinkButton = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  font: 'inherit',
  color: '#A11A1A',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
});
const StatusText = styled('div', {
  fontSize: '14px',
  variants: { alert: { true: { color: '#A11A1A', fontWeight: 600 } } },
});
const NA = styled('span', { color: '#5A5A5A', fontSize: '14px' });
const ActionButton = styled('button', {
  appearance: 'none',
  height: '36px',
  padding: '0 16px',
  border: '1px solid #44A735',
  borderRadius: '8px',
  background: '#FFFFFF',
  color: '#2E8722',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:disabled': {
    borderColor: '#DADADA',
    color: '#8A8A8A',
    cursor: 'not-allowed',
  },
});
const ActionCell = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
});
const Empty = styled('div', {
  padding: '48px 16px',
  textAlign: 'center',
  color: '#5A5A5A',
});

type Header = { key: string; label: string; sortKey?: SortColumn };

const USER: Header = { key: 'user', label: 'User Information', sortKey: 'id' };
const STUCK: Header = {
  key: 'stuck',
  label: 'Stuck for',
  sortKey: 'stuck_for',
};
const TAX: Header = { key: 'tax', label: 'Tax type', sortKey: 'tax_type' };
const ACTION: Header = { key: 'action', label: '' };

const STATUS_HEADER: Record<ColumnTab, Header> = {
  onboarding: {
    key: 'progress',
    label: 'Onboarding progress',
    sortKey: 'onboarding_step',
  },
  background_check: {
    key: 'bg_status',
    label: 'Background check status',
    sortKey: 'background_check_status',
  },
  i9: { key: 'i9_status', label: 'I-9 status', sortKey: 'i9_status_rank' },
};

export const headersFor = (
  tab: ColumnTab,
  compact: boolean,
  sortBy?: SortColumn,
): Header[] => {
  // With the side panel open the list narrows to the four columns the
  // design keeps (ticket WL-22), plus the column the list is sorted by when
  // it is one of the hidden ones: otherwise the rows are ordered by something
  // the table neither shows nor marks (the D-08 rule, for the panel).
  if (compact) {
    const kept = [USER, STATUS_HEADER[tab], STUCK];
    const sorted = fullHeaders(tab).find(
      (h) => h.sortKey === sortBy && !kept.includes(h),
    );
    return [...kept, ...(sorted ? [sorted] : []), ACTION];
  }
  return fullHeaders(tab);
};

const fullHeaders = (tab: ColumnTab): Header[] => {
  if (tab === 'onboarding')
    return [
      USER,
      STATUS_HEADER.onboarding,
      STUCK,
      { key: 'bg', label: 'BG check', sortKey: 'background_check_status' },
      { key: 'i9', label: 'I-9', sortKey: 'i9_status_rank' },
      TAX,
      // "N/5" is the completed count, so it sorts by that alone; the All
      // tab's `shifts` sort orders by claimed first (review D-07).
      { key: 'shifts', label: 'Shifts', sortKey: 'shifts_completed' },
      ACTION,
    ];
  return [USER, STATUS_HEADER[tab], STUCK, TAX, ACTION];
};

const fullName = (w: WorkerListItem) =>
  w.user?.full_name ??
  `${w.user?.first_name ?? ''} ${w.user?.last_name ?? ''}`.trim();

const VendorCell = ({
  status,
  testId,
}: {
  status: VendorStatus;
  testId: string;
}) => (
  <div data-testid={testId}>
    <StatusText alert={status.alert}>{status.label}</StatusText>
    <SegmentBar segments={status.segments} testId={`${testId}-bar`} />
    {status.warning && (
      <Warning>
        <WarningAmberIcon sx={{ fontSize: 16 }} aria-hidden="true" />
        {status.warning}
      </Warning>
    )}
  </div>
);

const ProgressCell = ({
  worker,
  activationShifts,
  onViewDetails,
  compact,
}: {
  worker: WorkerListItem;
  activationShifts: number;
  onViewDetails: () => void;
  compact: boolean;
}) => {
  const progress = worker.onboarding_progress;
  // A deactivated worker is neither onboarding nor activating: the cell says
  // so in the All tab's Status wording, before the completed branch below
  // reads their finished onboarding as "Activation" (BH-31).
  if (worker.current_status?.state === 'disabled')
    return (
      <StepText compact={compact} data-testid={`progress-${worker.id}`}>
        {statusPillLabel(worker.current_status)}
      </StepText>
    );
  if (!progress)
    return (
      <StepText compact={compact} data-testid={`progress-${worker.id}`}>
        Onboarding ({worker.onboarding_percentage ?? 0}%)
      </StepText>
    );

  if (progress.completed) {
    const completed = worker.shifts?.completed ?? 0;
    const segments: SegmentTone[] = Array.from(
      { length: activationShifts },
      (_, i) => (i < completed ? 'done' : 'todo'),
    );
    return (
      <div data-testid={`progress-${worker.id}`}>
        <StepText compact={compact}>
          Activation • {Math.min(completed, activationShifts)} of{' '}
          {activationShifts} shifts
        </StepText>
        <SegmentBar segments={segments} testId={`progressBar-${worker.id}`} />
        {progress.onboarding_completed_at && (
          <Small>
            Onboarding completed{' '}
            {formatLongDate(progress.onboarding_completed_at)}
          </Small>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`progress-${worker.id}`}>
      <StepText compact={compact}>
        Step {progress.current_step} of {progress.total_steps}:{' '}
        {progress.current_step_label}
      </StepText>
      <SegmentBar
        segments={progress.steps.map((s) => stepSegmentTone(s.status))}
        testId={`progressBar-${worker.id}`}
      />
      {progress.rejected && (
        <Warning data-testid={`rejected-${worker.id}`}>
          <WarningAmberIcon sx={{ fontSize: 16 }} aria-hidden="true" />
          Rejected.
          {/* Named for its worker: a screen reader's button list otherwise
              reads twenty identical "View details" (review R5A-08). */}
          <LinkButton
            type="button"
            aria-label={`View details for ${fullName(worker)}`}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
          >
            View details
          </LinkButton>
        </Warning>
      )}
    </div>
  );
};

const RowMenu = ({
  worker,
  onOpenProfile,
}: {
  worker: WorkerListItem;
  onOpenProfile: (id: number) => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label={`More actions for ${fullName(worker)}`}
        data-testid={`rowMenu-${worker.id}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onOpenProfile(worker.id);
          }}
        >
          Open profile
        </MenuItem>
      </Menu>
    </>
  );
};

// Every per-row button names its worker: a screen reader's button list
// otherwise reads twenty identical entries (review R5A-08, R6A-04).
const DisabledAction = ({
  label,
  testId,
  worker,
}: {
  label: string;
  testId: string;
  worker: WorkerListItem;
}) => (
  <CardTooltip title={NOT_AVAILABLE_REASON}>
    {/* Disabled buttons fire no events, so the span carries the tooltip. */}
    <span>
      <ActionButton
        type="button"
        disabled
        data-testid={testId}
        aria-label={`${label} for ${fullName(worker)} (${NOT_AVAILABLE_REASON.toLowerCase()})`}
      >
        {label}
      </ActionButton>
    </span>
  </CardTooltip>
);

const RowAction = ({
  tab,
  worker,
  onOpenPanel,
}: {
  tab: ColumnTab;
  worker: WorkerListItem;
  onOpenPanel: (id: number) => void;
}) => {
  const testId = `rowAction-${worker.id}`;
  if (tab === 'onboarding')
    return <DisabledAction label="Nudge" testId={testId} worker={worker} />;

  if (tab === 'background_check') {
    if (bgNeedsInvite(worker))
      return (
        <DisabledAction label="Resend invite" testId={testId} worker={worker} />
      );
    const candidate = worker.background_check?.candidate_id;
    return (
      <ActionButton
        type="button"
        data-testid={testId}
        aria-label={`View in Checkr for ${fullName(worker)}`}
        disabled={!candidate}
        title={candidate ? undefined : 'No Checkr candidate on file'}
        onClick={(e) => {
          e.stopPropagation();
          if (candidate)
            window.open(
              `${CHECKR_BASE_URL}/candidates/${candidate}`,
              '_blank',
              'noopener,noreferrer',
            );
        }}
      >
        View in Checkr
      </ActionButton>
    );
  }

  if (i9NeedsInvite(worker))
    return (
      <DisabledAction label="Resend invite" testId={testId} worker={worker} />
    );
  return (
    <ActionButton
      type="button"
      data-testid={testId}
      aria-label={`View details for ${fullName(worker)}`}
      onClick={(e) => {
        e.stopPropagation();
        onOpenPanel(worker.id);
      }}
    >
      View details
    </ActionButton>
  );
};

type Props = {
  tab: ColumnTab;
  workers: WorkerListItem[];
  sort: SortState;
  onSort: (column: SortColumn) => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
  // "Rejected. View details": open the worker at the rejected step.
  onViewRejected?: (id: number) => void;
  onOpenProfile: (id: number) => void;
  activationShifts: number;
  loading?: boolean;
  // The list request failed: the page shows the error and Retry, so an empty
  // body must not also claim "Nobody is stuck here" (review D-04).
  failed?: boolean;
  // A search, zip or filter is narrowing the list (BH-68).
  filtered?: boolean;
};

const OnboardingTable = ({
  tab,
  workers,
  sort,
  onSort,
  selectedId,
  onSelect,
  onViewRejected,
  onOpenProfile,
  activationShifts,
  loading = false,
  failed = false,
  filtered = false,
}: Props) => {
  const compact = selectedId !== null;
  const headers = headersFor(tab, compact, sort.by);
  const has = (key: string) => headers.some((h) => h.key === key);

  return (
    <TableScroll data-testid="onboardingTableScroll">
      <Table>
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <Th
                key={h.key}
                sortable={Boolean(h.sortKey)}
                compact={compact}
                aria-sort={
                  h.sortKey && sort.by === h.sortKey
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                onClick={h.sortKey ? () => onSort(h.sortKey!) : undefined}
                // Sortable headers take focus and sort on Enter or Space, so
                // the ticket's sorts work without a mouse (review D-13).
                tabIndex={h.sortKey ? 0 : undefined}
                onKeyDown={
                  h.sortKey
                    ? (e: KeyboardEvent<HTMLTableCellElement>) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        onSort(h.sortKey!);
                      }
                    : undefined
                }
                data-testid={`col-${h.key}`}
              >
                {h.label}
                {h.sortKey && (
                  <SortIcon
                    active={sort.by === h.sortKey}
                    direction={sort.direction}
                  />
                )}
              </Th>
            ))}
          </TableRow>
        </TableHead>
        {/* While a reload runs, the rows on screen belong to the previous
          range, filter or sort: mark them busy and fade them so they do not
          read as the new result (review R5B-05). */}
        <tbody
          data-testid="tableBody"
          aria-busy={loading && workers.length > 0 ? true : undefined}
          style={
            loading && workers.length > 0
              ? { opacity: 0.5, transition: 'opacity 150ms' }
              : undefined
          }
        >
          {workers.length === 0 && loading && (
            <tr>
              <td colSpan={headers.length}>
                <Empty data-testid="listLoading" aria-busy="true">
                  Loading workers…
                </Empty>
              </td>
            </tr>
          )}
          {workers.length === 0 && !loading && !failed && (
            <tr>
              <td colSpan={headers.length}>
                {/* BH-68: "Nobody is stuck here" reads as good news; when a
                    search or filter matched nobody it hid the real cause. */}
                <Empty data-testid="emptyState">
                  {filtered ? NO_MATCH_TEXT : 'Nobody is stuck here.'}
                </Empty>
              </td>
            </tr>
          )}
          {workers.map((w) => {
            const name = fullName(w);
            const activeAgo = formatActiveAgo(w.last_active_at);
            const bg = bgStatusFor(w);
            const i9 = i9StatusFor(w);
            const selected = selectedId === w.id;
            const vendorCleared =
              (tab === 'background_check' &&
                w.background_check?.status === 'clear') ||
              // A tentative_nc prints "Pending" (GRAVY-10988) but graphy has
              // settled step 4 for it (Product Q5), so it has no stuck count.
              (tab === 'i9' &&
                (i9?.label === 'Clear / Eligible' || i9Mismatch(w)));
            return (
              <Row
                key={w.id}
                data-worker-id={w.id}
                data-selected={selected}
                // aria-selected only counts on grid rows; a plain table row
                // announces the open worker through aria-current.
                aria-current={selected ? 'true' : undefined}
                selected={selected}
                onClick={() => onSelect(w.id)}
              >
                <Td>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Avatar
                      src={w.avatar_url}
                      firstName={w.user?.first_name}
                      lastName={w.user?.last_name}
                      a11yLabel={name || 'Worker avatar'}
                    />
                    <div style={{ minWidth: 0 }}>
                      <Name
                        type="button"
                        compact={compact}
                        title={name}
                        // WorkerPanel returns focus here when it closes.
                        data-testid={`workerName-${w.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(w.id);
                        }}
                      >
                        {name}
                      </Name>
                      <Muted>{formatPhone(w.user?.phone_number)}</Muted>
                      <ActiveLine today={activeAgo === 'active today'}>
                        {activeAgo}
                      </ActiveLine>
                    </div>
                  </div>
                </Td>
                <Td>
                  {tab === 'onboarding' && (
                    <ProgressCell
                      worker={w}
                      activationShifts={activationShifts}
                      compact={compact}
                      onViewDetails={() => (onViewRejected ?? onSelect)(w.id)}
                    />
                  )}
                  {tab === 'background_check' && (
                    <VendorCell status={bg} testId={`bgStatus-${w.id}`} />
                  )}
                  {tab === 'i9' &&
                    (i9 ? (
                      <VendorCell status={i9} testId={`i9Status-${w.id}`} />
                    ) : (
                      <NA data-testid={`i9Status-${w.id}`}>N/A</NA>
                    ))}
                </Td>
                <Td>
                  <StuckForPill worker={w} cleared={vendorCleared} />
                </Td>
                {has('bg') && (
                  <Td>
                    <StatusIcon
                      cleared={bgCheckCleared(w)}
                      testId={`bgCheckIcon-${w.id}`}
                      label={
                        bgCheckCleared(w)
                          ? 'Background check cleared'
                          : 'Background check not done'
                      }
                    />
                  </Td>
                )}
                {has('i9') && (
                  <Td>
                    {(w.tax_type ?? '').toLowerCase() === '1099' ? (
                      <NA data-testid={`i9Icon-${w.id}`}>N/A</NA>
                    ) : (
                      <StatusIcon
                        cleared={i9Cleared(w)}
                        testId={`i9Icon-${w.id}`}
                        label={i9IconLabel(w, 'I-9 not done')}
                      />
                    )}
                  </Td>
                )}
                {has('tax') && <Td>{formatTaxType(w.tax_type)}</Td>}
                {has('shifts') && (
                  <Td data-testid={`shifts-${w.id}`}>
                    {w.shifts?.completed ?? 0}/{activationShifts}
                  </Td>
                )}
                <Td>
                  <ActionCell>
                    <RowAction tab={tab} worker={w} onOpenPanel={onSelect} />
                    {tab !== 'onboarding' && !compact && (
                      <RowMenu worker={w} onOpenProfile={onOpenProfile} />
                    )}
                  </ActionCell>
                </Td>
              </Row>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
};

export default OnboardingTable;
